import { useState, useCallback } from 'react';
import { get, post, put } from '../utils/networkUtils';

export const useUserDetails = () => {
  const [user, setUser] = useState(null);
  const [entries, setEntries] = useState([]);
  const [ticketTransactions, setTicketTransactions] = useState([]);
  const [raffleEntries, setRaffleEntries] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [totalRaffleEntries, setTotalRaffleEntries] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUserDetails = useCallback(async (userId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await get(`/admin/users/${userId}`);
      setUser(response);
      return response;
    } catch (err) {
      console.error('Error fetching user details:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch user details';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUserEntries = useCallback(async (userId, { limit = 10, offset = 0 }) => {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });

      const response = await get(`/admin/users/${userId}/user-selections?${params.toString()}`);
      setEntries(response.selections || []);
      setTotalEntries(response.total || 0);
      return response;
    } catch (err) {
      console.error('Error fetching user entries:', err);
      throw err;
    }
  }, []);

  const fetchUserTicketTransactions = useCallback(async (userId, { limit = 10, offset = 0 }) => {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });

      const response = await get(`/admin/users/${userId}/ticket-transactions?${params.toString()}`);
      setTicketTransactions(response.transactions || []);
      setTotalTransactions(response.total || 0);
      return response;
    } catch (err) {
      console.error('Error fetching ticket transactions:', err);
      throw err;
    }
  }, []);

  const fetchUserRaffleEntries = useCallback(async (userId, { limit = 10, offset = 0 }) => {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });

      const response = await get(`/admin/users/${userId}/raffles?${params.toString()}`);
      setRaffleEntries(response.entries || []);
      setTotalRaffleEntries(response.total || 0);
      return response;
    } catch (err) {
      console.error('Error fetching raffle entries:', err);
      throw err;
    }
  }, []);

  const updateUserField = useCallback(async (userId, field, value) => {
    try {
      const response = await put(`/admin/users/${userId}`, {
        [field]: value,
      });
      
      // Update local state
      setUser(prev => ({
        ...prev,
        [field]: value,
      }));
      
      return response;
    } catch (err) {
      console.error(`Error updating user ${field}:`, err);
      throw err;
    }
  }, []);

  const addTickets = useCallback(async (userId, amount) => {
    try {
      const response = await post('/admin/tickets/add', {
        userId,
        amount,
      });
      
      return response;
    } catch (err) {
      console.error('Error adding tickets:', err);
      throw err;
    }
  }, []);

  return {
    user,
    entries,
    ticketTransactions,
    raffleEntries,
    totalEntries,
    totalTransactions,
    totalRaffleEntries,
    loading,
    error,
    fetchUserDetails,
    fetchUserEntries,
    fetchUserTicketTransactions,
    fetchUserRaffleEntries,
    updateUserField,
    addTickets,
  };
};
